import { emptySplitApi } from "./emptyApi"
import type { SupportedPlatform } from "./types/paid-media.types"
import type {
  AnalyzeCreativesRequest,
  AnalyzeCreativesResponse,
  CreativeArchiveRunResponse,
  ExecuteCandidateRequest,
  ExecuteCandidateResponse,
} from "./types/creative-management.types"

export const creativeManagementApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Run an archival playbook over the selected ad sets. Computes signals,
     * applies guardrails, and returns the proposed candidates (including
     * blocked ones) for review. Meta-only in v1; the platform path segment is
     * kept for symmetry with the paid-media routes.
     */
    analyzeCreatives: builder.mutation<
      AnalyzeCreativesResponse,
      { platform: SupportedPlatform; data: AnalyzeCreativesRequest }
    >({
      query: ({ platform, data }) => ({
        url: `creative-management/${platform}/analyze`,
        method: "POST",
        body: data,
      }),
    }),

    /**
     * Record the reviewer's decision for one candidate. ACCEPTED archives the
     * ad on Meta (and upserts the creative registry entry); REJECTED records
     * the decision with outcome SKIPPED. Per-ad failures surface as outcome
     * FAILED in the response body, not as an HTTP error.
     */
    executeCreativeCandidate: builder.mutation<
      ExecuteCandidateResponse,
      { candidateId: string; data: ExecuteCandidateRequest }
    >({
      query: ({ candidateId, data }) => ({
        url: `creative-management/candidates/${candidateId}/execute`,
        method: "POST",
        body: data,
      }),
      // An accepted candidate archives the DB-backed Ad row server-side, so
      // the AdBlaster Ads tab list cache needs a refresh.
      invalidatesTags: [{ type: "Ad", id: "COMPANY_LIST" }],
    }),

    getCreativeArchiveRun: builder.query<
      CreativeArchiveRunResponse,
      { runId: string }
    >({
      query: ({ runId }) => `creative-management/runs/${runId}`,
    }),
  }),
})

export const {
  useAnalyzeCreativesMutation,
  useExecuteCreativeCandidateMutation,
  useGetCreativeArchiveRunQuery,
} = creativeManagementApi
